/**
 * The review before a save (D10): what will be stored, what it replaces, and
 * the server's rendering of exactly that draft.
 *
 * ONLY ON A CURRENT, SUCCESSFUL PREVIEW. The tab opens this panel after the
 * server rendered the draft as it stands; the subject and the frame here are
 * that rendering, not the editor's text. "Version speichern" is disabled the
 * moment the preview stops being the draft's, and while a save is under way.
 *
 * NO VERSION NUMBER IS PROMISED. The server allocates the next number; the
 * panel says "eine neue Version" and names what it replaces (the current
 * version, or the system template), nothing it would have to guess.
 *
 * A REFUSED SAVE STAYS HERE. The server's sentence, its issues as German
 * lines (never the raw structure), and on a version conflict the way to load
 * the current template — which discards the draft, so the button says so.
 */
import React, { forwardRef, useId } from "react";

import { templateIssueLines } from "./automationTemplateLabels";

const PRIMARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-primary bg-primary px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const TERM = "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

const AutomationTemplateReview = forwardRef(function AutomationTemplateReview(
  { replaces, result, saving, canSave, saveError, onBack, onSave, onReload },
  headingRef
) {
  const headingId = useId();
  const issueLines = saveError ? templateIssueLines(saveError) : [];
  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-[14px] border border-primary/40 bg-card p-3.5"
    >
      <h3
        id={headingId}
        ref={headingRef}
        tabIndex={-1}
        className="m-0 text-[14px] font-semibold text-foreground focus-visible:outline-none"
      >
        Änderungen prüfen
      </h3>
      <dl className="m-0 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex flex-col gap-0.5">
          <dt className={TERM}>Gespeichert wird</dt>
          <dd className="m-0 text-[12.5px] text-foreground">
            Eine neue Version der eigenen Vorlage
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className={TERM}>Sie ersetzt</dt>
          <dd className="m-0 text-[12.5px] text-foreground">{replaces}</dd>
        </div>
      </dl>
      <dl className="m-0 flex flex-col gap-0.5">
        <dt className={TERM}>Betreff</dt>
        <dd className="m-0 break-words text-[13px] font-semibold text-foreground">
          {result?.rendered_subject}
        </dd>
      </dl>
      {result ? (
        <iframe
          title="Geprüfte Vorschau der E-Mail"
          srcDoc={result.rendered_html}
          sandbox=""
          className="h-[320px] w-full rounded-[10px] border border-border bg-background"
        />
      ) : null}
      <p className="m-0 text-[12px] text-muted-foreground">
        Ab dem Speichern gilt diese Vorlage für neue E-Mails dieser Automatisierung.
      </p>
      {saveError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="m-0 text-[12.5px] text-destructive">
            {saveError.message}
          </p>
          {issueLines.length ? (
            <ul className="m-0 flex flex-col gap-0.5 pl-4 text-[12px] text-destructive">
              {issueLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          {saveError.code === "template_version_conflict" ? (
            <button type="button" onClick={onReload} className={SECONDARY_BUTTON}>
              Aktuelle Vorlage laden und Entwurf verwerfen
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onBack} disabled={saving} className={SECONDARY_BUTTON}>
          Zurück zur Bearbeitung
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !canSave}
          className={PRIMARY_BUTTON}
        >
          {saving ? "Wird gespeichert …" : "Version speichern"}
        </button>
      </div>
    </section>
  );
});

export default AutomationTemplateReview;
